import { Injectable } from '@angular/core';
import { catchError, of, forkJoin } from 'rxjs';
import { Identified } from '../../models/identified.model';
import { HistoryChange, HistoryChangeBuilder } from '../../models/history-change.model';
import { SectionLayoutMetadataBase } from '../../../common-shared-module/model/dyn-form/form-metadata/layout/section-layout.model';
import { DynFormService } from '../../../common-shared-module/services/dyn-form/dyn-form.service';
import { DynFormSectionResponseBase } from '../../../common-shared-module/model/dyn-form/api/dyn-form-section-response.model';

@Injectable({
    providedIn: 'root'
})
export class SectionHistoryService {

    maxHistorySize: number = 40
    history: HistoryChange[] = []
    currentHistoryChange: HistoryChange | null = null

    lastSectionToSave: SectionLayoutMetadataBase | null = null
    token: number | null = null

    constructor(private dynFormService: DynFormService) { }

    clear(): void {
        this.history = []
        this.currentHistoryChange = null
    }

    currentState(): SectionLayoutMetadataBase | null {
        if (this.currentHistoryChange == null) return null
        return this.currentHistoryChange.toSectionLayoutMetadata()
    }

    init(change: HistoryChange): void {
        this.history.push(change)
        this.currentHistoryChange = change
    }

    selectionId(): string | null {
        if (this.currentHistoryChange == null) return null
        return this.currentHistoryChange.selectionId()
    }

    do(section: SectionLayoutMetadataBase, identified: Identified | string | null): void {
        if (this.currentHistoryChange == null) return

        let selectedId: string | null = null
        if (typeof identified == 'string') selectedId = identified
        else if (identified) selectedId = identified.id()

        let newChange: HistoryChange = new HistoryChangeBuilder().section(section).sectionForm(this.currentHistoryChange.sectionForm()).selectionId(selectedId).build()

        this.clearRedoChanges()
        this.history.push(newChange)
        
        if (this.history.length > this.maxHistorySize) this.history.shift()

        this.currentHistoryChange = newChange
        this.saveSection(section)
    } 

    undoAvailable(): boolean {
        if (this.currentHistoryChange == null || this.history.length == 0) return false
        return this.currentHistoryChange != this.history[0] && this.history.length > 1
    }

    redoAvailable(): boolean {
        if (this.currentHistoryChange == null || this.history.length == 0) return false
        return this.currentHistoryChange != this.history[this.history.length - 1]
    }

    clearRedoChanges() {
        if (this.currentHistoryChange == null) return

        let index = this.history.findIndex(p => p == this.currentHistoryChange)
        this.history.splice(index + 1, this.history.length - index)
    }

    unDo(): SectionLayoutMetadataBase | null {
        let index = this.history.findIndex(p => p == this.currentHistoryChange)
        if (index == 0 && this.currentHistoryChange) return null

        this.currentHistoryChange = this.history[index - 1]
        let section: SectionLayoutMetadataBase = this.currentHistoryChange.toSectionLayoutMetadata()
        this.saveSection(section)
        return section
    }

    reDo(): SectionLayoutMetadataBase | null {
        let index = this.history.findIndex(p => p == this.currentHistoryChange)
        if (index + 1 == this.history.length) return null

        this.currentHistoryChange = this.history[index + 1]
        let section: SectionLayoutMetadataBase = this.currentHistoryChange.toSectionLayoutMetadata()
        this.saveSection(section)
        return section
    }

    saveSection(sectionLayout: SectionLayoutMetadataBase) {
        if (this.currentHistoryChange == null) return

        this.lastSectionToSave = sectionLayout
        if (this.token == null) {
            this.token = Math.floor(Math.random() * 1000000)
            this.currentHistoryChange?.sectionNameAndRequired(sectionLayout.name, sectionLayout.required)

            let currentToken = this.token
            forkJoin<[DynFormSectionResponseBase, boolean]>([
                this.dynFormService.putDynFormSection(this.currentHistoryChange.sectionForm()),
                this.dynFormService.saveSection(this.currentHistoryChange.sectionForm().id, sectionLayout)
            ]).pipe(catchError(e => {return of ([])})).subscribe(r => {
                if (currentToken != this.token) {
                    this.token = null
                    setTimeout(() => { this.saveSection(<SectionLayoutMetadataBase> this.lastSectionToSave)}, 1)
                } else {
                    this.token = null
                    this.lastSectionToSave = null
                }
            })
        } else {
            this.token = Math.floor(Math.random() * 1000000)
        }
    }
}
